import { useEffect, useRef } from 'react';
import useSWRInfinite from 'swr/infinite';
import getConfig from 'next/config';

import { FeatureFlags } from 'featureFlags/constants';
import { fetcher } from 'shared/utils/swrDataFetcher';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { IProductSearch } from 'shared/types/ProductSearch';
import { SHOE_CONDITIONS } from 'shared/enums/ConditionsEnums';
import { requestService } from 'shared/utils/services/requestService';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useLocation } from 'shared/hooks/useLocation';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { VariantSectionContent } from 'productTemplate/components/buyBar/VariantSectionContent';
import { useIsElementInViewport } from 'shared/hooks/useIsElementInViewport';
import { Loader } from 'shared/components/Loader';
import { IMetaData } from 'shared/types/MetaData';

interface IUsedSection {
  className?: string;
  selectedSize: number;
  onVariantsChange?: (arg: IProductSearch[]) => void;
}

const fetchData = (url) => requestService({ url, method: 'GET' });

export const UsedSection = ({ className, selectedSize, onVariantsChange }: IUsedSection) => {
  const { id, slug } = useStoreSelector((state) => state.pdp.productTemplate);
  const { selectedCountryCode }: { selectedCountryCode: string } = useLocation();

  const { publicRuntimeConfig } = getConfig();
  const { SPIKE_KEY } = publicRuntimeConfig;

  const productsRef = useRef<Array<{ metadata?: IMetaData; products: IProductSearch[] }>>();
  const isSpikeMitigateEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SPIKE_MITIGATION);

  const { data, error, isLoading, setSize, size } = useSWRInfinite(
    (_, prevData) => {
      if (prevData && !prevData?.metadata?.nextPage) return null;

      const searchUrl = [
        '/web-api/v1/products/search',
        `?productTemplateId=${id}&shoeCondition=${SHOE_CONDITIONS.USED}&size=${selectedSize}&sortBy=size&countryCode=${selectedCountryCode}`,
        prevData?.metadata?.nextPage ? `&page=${prevData?.metadata?.nextPage as string}` : '',
      ].join('');

      return isSpikeMitigateEnabled ? { url: searchUrl, key: SPIKE_KEY } : searchUrl;
    },
    isSpikeMitigateEnabled ? fetcher : fetchData,
    { fallbackData: productsRef.current ? productsRef.current : [] },
  );

  const isLoadingInitialData = (!data && !error) || isLoading;
  const hasMoreData = Boolean(data?.[size - 1]?.metadata?.nextPage);
  const isLoadingMore = size > 0 && data && typeof data[size - 1] === 'undefined';
  const products = data?.flatMap((_data) => _data?.products);

  if (data !== undefined && productsRef) {
    productsRef.current = data;
  }

  const observer = useRef();
  const shouldLoadMore = useIsElementInViewport(observer);

  useEffect(() => {
    if (shouldLoadMore && hasMoreData) {
      void setSize(size + 1);
    }
  }, [shouldLoadMore, setSize]);

  useEffect(() => {
    onVariantsChange?.(products);
  }, [products, onVariantsChange]);

  useEffect(() => {
    if (data) {
      sendTrackingEvent('PDP_GRID_VIEW', {
        condition_category: 'used',
        product_count: products?.length,
        product_template_slug: slug,
        size: selectedSize,
      });
    }
  }, [data, selectedSize, slug]);

  if (isLoadingInitialData) {
    return <Loader style={{ width: '160px', margin: '72px auto' }} />;
  }

  return (
    <>
      <VariantSectionContent className={className} variants={products} />
      {isLoadingMore && (
        <Loader
          className="loader"
          data-qa="used_section_loader"
          style={{ marginBlock: '10px', marginInline: 'auto' }}
        />
      )}
      <div ref={observer} />
    </>
  );
};
